import { DAYS_RESOLVED_TO_CLOSED_RANGE, type UpdateQueueSettingsInput } from '@easyhelp/shared';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { Controller, type Control } from 'react-hook-form';
import { SettingsSection } from './SettingsSection';

export interface QueueRulesSectionProps {
  control: Control<UpdateQueueSettingsInput>;
}

interface RuleSwitchProps {
  control: Control<UpdateQueueSettingsInput>;
  name: 'requirePriorityBeforeReply' | 'requireDescriptionOnClose';
  label: string;
  /** Cosa succede con la regola attiva e cosa con la regola disattiva. */
  consequence: string;
}

/** Interruttore di una regola, con il testo che ne spiega la conseguenza. */
function RuleSwitch({ control, name, label, consequence }: RuleSwitchProps) {
  const helperId = `rule-${name}-consequence`;
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={field.value}
                onChange={(_event, checked) => field.onChange(checked)}
                onBlur={field.onBlur}
                inputRef={field.ref}
                name={field.name}
                slotProps={{ input: { 'aria-describedby': helperId } }}
              />
            }
            label={label}
          />
          <FormHelperText id={helperId} sx={{ ml: 0 }}>
            {consequence}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
}

/** Regole della coda: priorità prima di rispondere, descrizione alla chiusura, giorni da Risolto a Chiuso. */
export function QueueRulesSection({ control }: QueueRulesSectionProps) {
  return (
    <SettingsSection
      id="queue-settings-rules"
      title="Regole della coda"
      description="Vincoli su come si lavorano i ticket."
    >
      <RuleSwitch
        control={control}
        name="requirePriorityBeforeReply"
        label="Priorità obbligatoria prima di rispondere"
        consequence="Attiva: non si risponde a un ticket senza priorità. Disattiva: i ticket possono restare non classificati e l’ordine per urgenza non è affidabile."
      />
      <RuleSwitch
        control={control}
        name="requireDescriptionOnClose"
        label="Descrizione obbligatoria alla chiusura"
        consequence="Attiva: «Invia e chiudi» richiede la descrizione della correzione (almeno 10 caratteri), che entra nell’email a chi ha segnalato. Disattiva: si può chiudere senza descrizione e l’email dichiara che non ci sono dettagli."
      />
      <Controller
        name="daysResolvedToClosed"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            name={field.name}
            inputRef={field.ref}
            onBlur={field.onBlur}
            value={Number.isNaN(field.value) ? '' : field.value}
            onChange={(event) => field.onChange(event.target.value === '' ? Number.NaN : Number(event.target.value))}
            label="Giorni da Risolto a Chiuso"
            type="number"
            required
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error?.message ??
              'Dopo questi giorni senza replica il ticket si chiude da solo; vale per le chiusure successive.'
            }
            slotProps={{
              htmlInput: { min: DAYS_RESOLVED_TO_CLOSED_RANGE.min, max: DAYS_RESOLVED_TO_CLOSED_RANGE.max, step: 1 },
            }}
            sx={{ maxWidth: 280 }}
          />
        )}
      />
    </SettingsSection>
  );
}
